/** Maps Firebase Auth errors to i18n keys and resolves email sign-up conflicts. */
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';

const INVALID_CREDENTIALS_KEY = 'auth.error.invalidCredentials';

const AUTH_CODE_TO_I18N_KEY = {
  'auth/email-already-in-use': 'auth.error.emailAlreadyInUse',
  'auth/account-exists-with-different-credential': 'auth.error.googleUsePassword',
  'auth/weak-password': 'auth.error.weakPassword',
  'auth/invalid-email': 'auth.error.invalidEmail',
  'auth/invalid-credential': INVALID_CREDENTIALS_KEY,
  'auth/user-not-found': INVALID_CREDENTIALS_KEY,
  'auth/wrong-password': INVALID_CREDENTIALS_KEY,
  'auth/too-many-requests': 'auth.error.tooManyRequests',
  'auth/network-request-failed': 'auth.error.network',
  'auth/empty-email': 'auth.error.emptyEmail',
  'auth/empty-password': 'auth.error.emptyPassword',
};

/**
 * Maps a Firebase Auth error (or validation error with `code`) to an i18n key.
 * @param {{ code?: string } | null | undefined} error
 * @returns {string}
 */
export const getAuthErrorKey = (error) => {
  const code = error?.code;
  if (code && AUTH_CODE_TO_I18N_KEY[code]) {
    return AUTH_CODE_TO_I18N_KEY[code];
  }
  return 'auth.error.unknown';
};

const normalizeEmail = (email) => (email ?? '').trim().toLowerCase();

const SIGN_UP_CONFLICT_CODE = 'auth/email-already-in-use';
const GOOGLE_CREDENTIAL_CONFLICT_CODE =
  'auth/account-exists-with-different-credential';

/**
 * @param {{ code?: string, customData?: { email?: string } } | null | undefined} error
 * @param {string} [emailHint]
 * @returns {Promise<string>}
 */
async function resolveSignUpConflictKey(error, emailHint) {
  const normalizedEmail = normalizeEmail(emailHint ?? error?.customData?.email);
  if (!normalizedEmail) {
    return 'auth.error.emailSignUpConflictGeneric';
  }
  try {
    const methods = await fetchSignInMethodsForEmail(auth, normalizedEmail);
    if (methods.includes('google.com')) {
      return 'auth.error.emailLinkedToGoogle';
    }
    if (methods.includes('password')) {
      return 'auth.error.emailHasPasswordAccount';
    }
  } catch (lookupError) {
    console.error(
      'AuthService: Could not resolve sign-in methods for conflict:',
      lookupError?.code ?? lookupError?.message,
    );
  }
  return 'auth.error.emailSignUpConflictGeneric';
}

/**
 * Resolves provider-specific i18n keys for email/sign-in method conflicts.
 * @param {{ code?: string, customData?: { email?: string } } | null | undefined} error
 * @param {string} [emailHint] — form email or Firebase customData.email
 * @returns {Promise<string>}
 */
export const resolveAuthErrorKey = async (error, emailHint) => {
  const code = error?.code;
  if (code === GOOGLE_CREDENTIAL_CONFLICT_CODE) {
    return AUTH_CODE_TO_I18N_KEY[code];
  }
  if (code === SIGN_UP_CONFLICT_CODE) {
    return resolveSignUpConflictKey(error, emailHint);
  }
  return getAuthErrorKey(error);
};
